import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FormikTextField, FormikZipCodeField } from "@shared";
import { FormikProps } from "formik";

import {
  AmsParametersFormValues,
  AmsVidMaxLength,
  AmsZipCodeMaxLength,
} from "@/pages/system-settings/tabs/ams-parameters/AmsParametersSettings.types";
import { useTranslation } from "@/translations";

interface Props {
  formik: FormikProps<AmsParametersFormValues>;
  onAdd: () => void;
  onDelete: (index: number) => void;
}

// A medium IconButton (40px) plus the 16px inset that keeps it off the table border. Stated because
// tableLayout is fixed, so the column would otherwise squeeze the button.
const ActionColumnWidth = 56;

export function AmsMappingTable({ formik, onAdd, onDelete }: Props) {
  const { t } = useTranslation();
  const mappings = formik.values.mappings;

  return (
    <Stack gap={1.5} alignItems="flex-start">
      <TableContainer
        sx={{
          width: "100%",
          borderRadius: 1.5,
          overflow: "hidden",
          border: (theme) => `1px solid ${theme.palette.fsw["grey-50"]}`,
        }}
      >
        <Table
          size="small"
          sx={{
            tableLayout: "fixed",
            "& thead th": {
              fontWeight: 700,
              backgroundColor: (theme) => theme.palette.fsw["grey-25"],
              py: 1.25,
            },
            "& tbody td": {
              verticalAlign: "top",
              py: 1.25,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("settings.amsParameters.zipCode")}</TableCell>
              <TableCell>{t("settings.amsParameters.vid")}</TableCell>
              <TableCell width={ActionColumnWidth} />
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("settings.amsParameters.noMappings")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              mappings.map((mapping, index) => (
                <TableRow key={mapping.id ?? `new-${index}`}>
                  <TableCell>
                    <FormikZipCodeField
                      formik={formik}
                      path={`mappings.${index}.zipCode`}
                      required
                      slotProps={{
                        input: {
                          inputProps: {
                            maxLength: AmsZipCodeMaxLength,
                            "aria-label": t("settings.amsParameters.zipCode"),
                          },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormikTextField
                      formik={formik}
                      path={`mappings.${index}.vid`}
                      required
                      slotProps={{
                        input: {
                          inputProps: {
                            maxLength: AmsVidMaxLength,
                            "aria-label": t("settings.amsParameters.vid"),
                          },
                        },
                      }}
                    />
                  </TableCell>
                  {/* No left padding: the VID cell's own padding is the gap to the icon, so the two do not
                      stack into a wide gutter. */}
                  <TableCell sx={{ pl: 0, pr: 2 }}>
                    <IconButton
                      onClick={() => onDelete(index)}
                      size="medium"
                      aria-label={t("settings.amsParameters.removeMapping")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={onAdd}>
        {t("settings.amsParameters.addMapping")}
      </Button>
    </Stack>
  );
}
